import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { jobs as jobsApi, seekAds, hireRequests, tokens } from '../utils/api';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';

const statusColor = { OPEN: 'chip-green', ASSIGNED: 'chip-blue', COMPLETED: 'chip-teal', CLOSED: 'chip-gray', PENDING_VERIFY: 'chip-amber', HIRED: 'chip-blue', ACTIVE: 'chip-green', PAUSED: 'chip-amber' };

export default function Dashboard() {
  const { t, user } = useApp();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') || 'overview';

  const [tab, setTab] = useState(tabFromUrl);
  const [myJobs, setMyJobs] = useState([]);
  const [applied, setApplied] = useState([]);
  const [seekList, setSeekList] = useState([]);
  const [hireReqs, setHireReqs] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applicantJob, setApplicantJob] = useState(null);
  const [applicants, setApplicants] = useState([]);

  const isPoster = user?.role === 'POSTER';
  const isSeeker = user?.role === 'SEEKER';

  useEffect(() => { if (!user) navigate('/login'); }, [user, navigate]);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [bal] = await Promise.all([tokens.balance()]);
      setBalance(bal.balance);

      if (isPoster) {
        const [jRes, hrRes] = await Promise.all([
          jobsApi.list({ poster_id: user.id }),
          hireRequests.my(),
        ]);
        setMyJobs(jRes.jobs ?? []);
        setHireReqs(hrRes.hireRequests ?? []);
      }
      if (isSeeker) {
        const [aRes, sRes] = await Promise.all([
          jobsApi.list({ applicant_id: user.id }),
          seekAds.my(),
        ]);
        setApplied(aRes.jobs ?? []);
        setSeekList(sRes.seekAds ?? []);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [user, isPoster, isSeeker]);

  useEffect(() => { load(); }, [load]);

  const switchTab = t => { setTab(t); setSearchParams({ tab: t }); };

  const openApplicants = async job => {
    setApplicantJob(job);
    try {
      const res = await jobsApi.applicants(job.id);
      setApplicants(res.applicants ?? []);
    } catch { setApplicants([]); }
  };

  const handleSelect = async (jobId, candidateId) => {
    try {
      await jobsApi.select(jobId, { candidateId });
      toast.success('Candidate selected!');
      setApplicantJob(null);
      setMyJobs(js => js.map(j => j.id === jobId ? { ...j, status: 'ASSIGNED' } : j));
    } catch (err) { toast.error(err.error || t('error_occurred')); }
  };

  const handleMarkComplete = async jobId => {
    try {
      await jobsApi.markComplete(jobId);
      toast.success('Marked as complete!');
      setApplied(js => js.map(j => j.id === jobId ? { ...j, status: 'PENDING_VERIFY' } : j));
    } catch (err) { toast.error(err.error || t('error_occurred')); }
  };

  const handleVerify = async jobId => {
    try {
      await jobsApi.verify(jobId);
      toast.success('Job verified!');
      setMyJobs(js => js.map(j => j.id === jobId ? { ...j, status: 'COMPLETED' } : j));
    } catch (err) { toast.error(err.error || t('error_occurred')); }
  };

  const handleSeekStatus = async (id, action) => {
    try {
      if (action === 'pause') await seekAds.pause(id);
      if (action === 'activate') await seekAds.activate(id);
      if (action === 'close') await seekAds.close(id);
      toast.success('Updated!');
      setSeekList(ls => ls.map(a => a.id === id ? { ...a, status: action === 'pause' ? 'PAUSED' : action === 'close' ? 'CLOSED' : 'ACTIVE' } : a));
    } catch (err) { toast.error(err.error || t('error_occurred')); }
  };

  const handleHireRespond = async (id, status) => {
    try {
      const seekAdsApi = (await import('../utils/api')).hireRequests;
      await seekAdsApi.respond(id, status);
      toast.success(status === 'ACCEPTED' ? 'You marked yourself hired! 🎉' : 'Request declined.');
      setSeekList(ls => ls.map(a => {
        const hr = hireReqs.find(h => h.id === id);
        return hr && a.id === hr.seekAdId ? { ...a, status: 'HIRED', isHired: true } : a;
      }));
      load();
    } catch (err) { toast.error(err.error || t('error_occurred')); }
  };

  if (loading) return <div className="page-loader"><div className="spinner" /></div>;

  const displayJobs = isPoster ? myJobs : applied;
  const totalOpen = displayJobs.filter(j => j.status === 'OPEN').length;
  const totalInProject = displayJobs.filter(j => j.status === 'ASSIGNED').length;
  const totalPending = displayJobs.filter(j => j.status === 'PENDING_VERIFY').length;
  const totalDone = displayJobs.filter(j => j.status === 'COMPLETED').length;

  const tabs = isPoster
    ? [['overview', 'Overview', '📊'], ['my_jobs', 'My Jobs', '💼'], ['hire_requests', 'Hire Requests', '🤝']]
    : [['overview', 'Overview', '📊'], ['applied_jobs', 'Applied', '📝'], ['my_seek_ads', 'Seek Ads', '📢'], ['hire_requests', 'Hire Requests', '🤝']];


  return (
    <div className="dashboard-layout">
      {/* Sidebar */}
      <div className="sidebar">
        <div className="sidebar-menu">
          <div className="section-title hide-mobile" style={{ fontSize: '11px', marginBottom: '8px' }}>Menu</div>
          {tabs.map(([k, label, icon]) => (
            <button key={k} onClick={() => switchTab(k)}
              className={`sidebar-link ${tab === k ? 'active' : ''}`}>
              <span style={{ fontSize: '16px' }}>{icon}</span>
              <span>{label}</span>
            </button>
          ))}
        </div>
        
        <div className="sidebar-footer">
          <Link to="/profile" className="sidebar-link" style={{ padding: '8px 0' }}>
            👤 Profile Settings
          </Link>
          <Link to="/wallet" className="sidebar-link" style={{ padding: '8px 0' }}>
            🪙 My Wallet
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="dashboard-content fade-in">
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1 style={{ fontSize: '26px', fontWeight: 700 }}>{t('nav_dashboard')}</h1>
            <p style={{ color: 'var(--text3)', fontSize: '14px', marginTop: '4px' }}>Welcome back, <span style={{ color: 'var(--text)', fontWeight: 600 }}>{user?.fullName}</span></p>
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <div className="token-badge" style={{ height: '38px' }}>🪙 {balance ?? '—'}</div>
            {isPoster && <Link to="/post-job" className="btn btn-primary">+ {t('post_job')}</Link>}
            {isSeeker && <Link to="/post-seek-ad" className="btn btn-primary">+ {t('post_seek')}</Link>}
          </div>
        </div>

        {/* Stats */}
        <div className="stat-grid" style={{ marginBottom: '32px' }}>
          <div className="stat-card brand">
            <div className="val">{balance ?? '—'}</div>
            <div className="lbl">Tokens</div>
          </div>
          <div className="stat-card"><div className="val">{totalOpen}</div><div className="lbl">Open</div></div>
          <div className="stat-card"><div className="val">{totalInProject}</div><div className="lbl">In Project</div></div>
          <div className="stat-card"><div className="val">{totalPending}</div><div className="lbl">Pending</div></div>
          <div className="stat-card"><div className="val">{totalDone}</div><div className="lbl">Completed</div></div>
          {isSeeker && <div className="stat-card"><div className="val">{seekList.length}</div><div className="lbl">Seek ads</div></div>}
        </div>

        <div className="divider" style={{ margin: '32px 0' }} />


      {/* OVERVIEW */}
      {tab === 'overview' && (
        <div className="stagger">
          {displayJobs.slice(0, 5).map(job => (
            <div key={job.id} className="card" style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
              <div>
                <div style={{ fontWeight: 600 }}>{job.title}</div>
                <div style={{ fontSize: '12px', color: 'var(--text3)', marginTop: '2px' }}>৳{Number(job.budget).toLocaleString()} · {job.location}</div>
              </div>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <span className={`chip ${statusColor[job.status] || 'chip-gray'}`}>{job.status}</span>
                {isPoster && job.status === 'OPEN' && <button className="btn btn-outline btn-sm" onClick={() => openApplicants(job)}>{job.applicantCount ?? 0} applicants</button>}
                {isPoster && job.status === 'PENDING_VERIFY' && <button className="btn btn-success btn-sm" onClick={() => handleVerify(job.id)}>{t('verify_complete')}</button>}
                {isSeeker && job.status === 'ASSIGNED' && <button className="btn btn-primary btn-sm" onClick={() => handleMarkComplete(job.id)}>{t('mark_complete')}</button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* MY JOBS / APPLIED */}
      {(tab === 'my_jobs' || tab === 'applied_jobs') && (
        <div className="stagger">
          {displayJobs.length === 0 ? <div className="empty-state">{t('no_jobs_found')}</div>
            : displayJobs.map(job => (
              <div key={job.id} className="card" style={{ marginBottom: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{job.title}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text3)', marginTop: '4px' }}>{job.type} · {job.category} · ৳{Number(job.budget).toLocaleString()} · 📍{job.location}</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-end' }}>
                    <span className={`chip ${statusColor[job.status] || 'chip-gray'}`}>{job.status}</span>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <Link to={`/jobs/${job.id}`} className="btn btn-outline btn-sm">View</Link>
                      {isPoster && job.status === 'OPEN' && <button className="btn btn-outline btn-sm" onClick={() => openApplicants(job)}>{job.applicantCount ?? 0} applicants</button>}
                      {isPoster && job.status === 'PENDING_VERIFY' && <button className="btn btn-success btn-sm" onClick={() => handleVerify(job.id)}>{t('verify_complete')}</button>}
                      {isSeeker && job.status === 'ASSIGNED' && <button className="btn btn-primary btn-sm" onClick={() => handleMarkComplete(job.id)}>{t('mark_complete')}</button>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}

      {/* SEEK ADS (seeker) */}
      {tab === 'my_seek_ads' && isSeeker && (
        <div className="stagger">
          <div style={{ marginBottom: '14px', display: 'flex', justifyContent: 'flex-end' }}>
            <Link to="/post-seek-ad" className="btn btn-primary btn-sm">+ New seek ad</Link>
          </div>
          {seekList.length === 0 ? (
            <div className="empty-state">
              <p style={{ fontSize: '32px', marginBottom: '8px' }}>📢</p>
              <p>You haven't published any seek ads yet.</p>
              <Link to="/post-seek-ad" className="btn btn-primary" style={{ marginTop: '14px', display: 'inline-flex' }}>Publish your first seek ad</Link>
            </div>
          ) : seekList.map(ad => (
            <div key={ad.id} className="card" style={{ marginBottom: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{ad.title}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text3)', marginTop: '4px' }}>
                    ৳{Number(ad.rate).toLocaleString()}/{ad.rateType?.toLowerCase()} · {ad.category} · 📍{ad.location}
                  </div>
                  {ad.skills?.length > 0 && (
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '6px' }}>
                      {ad.skills.slice(0, 4).map(s => <span key={s} className="chip chip-teal" style={{ fontSize: '10px' }}>{s}</span>)}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-end' }}>
                  <span className={`chip ${statusColor[ad.status] || 'chip-gray'}`}>{ad.isHired ? 'HIRED' : ad.status}</span>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <Link to={`/seek-ads/${ad.id}`} className="btn btn-outline btn-sm">View</Link>
                    {ad.status === 'ACTIVE' && <button className="btn btn-outline btn-sm" onClick={() => handleSeekStatus(ad.id, 'pause')}>Pause</button>}
                    {ad.status === 'PAUSED' && <button className="btn btn-success btn-sm" onClick={() => handleSeekStatus(ad.id, 'activate')}>Activate</button>}
                    {ad.status !== 'CLOSED' && <button className="btn btn-danger btn-sm" onClick={() => handleSeekStatus(ad.id, 'close')}>Close</button>}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* HIRE REQUESTS */}
      {tab === 'hire_requests' && (
        <div className="stagger">
          {/* Seeker: incoming hire requests */}
          {isSeeker && (
            seekList.length === 0
              ? <div className="empty-state"><p>Publish a seek ad to receive hire requests.</p></div>
              : seekList.map(ad => {
                const adReqs = hireReqs.filter(h => h.seekAdId === ad.id || h.seek_ad_id === ad.id);
                if (adReqs.length === 0) return null;
                return (
                  <div key={ad.id} style={{ marginBottom: '20px' }}>
                    <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--text2)', marginBottom: '8px' }}>
                      Requests for: {ad.title}
                    </div>
                    {adReqs.map(hr => (
                      <div key={hr.id} className="card" style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                        <div>
                          <div style={{ fontWeight: 500, fontSize: '14px' }}>{hr.poster_name}</div>
                          <div style={{ fontSize: '12px', color: 'var(--text3)', marginTop: '2px' }}>{hr.message || 'No message'}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text3)', marginTop: '2px' }}>{new Date(hr.created_at).toLocaleDateString()}</div>
                        </div>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <span className={`chip ${hr.status === 'PENDING' ? 'chip-amber' : hr.status === 'ACCEPTED' ? 'chip-green' : 'chip-red'}`}>{hr.status}</span>
                          {hr.status === 'PENDING' && (
                            <>
                              <button className="btn btn-success btn-sm" onClick={() => handleHireRespond(hr.id, 'ACCEPTED')}>{t('accept')}</button>
                              <button className="btn btn-danger btn-sm" onClick={() => handleHireRespond(hr.id, 'REJECTED')}>{t('decline')}</button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })
          )}
          {/* Poster: sent hire requests */}
          {isPoster && (
            hireReqs.length === 0
              ? <div className="empty-state"><p>You haven't sent any hire requests yet. <Link to="/find-hire">Browse seekers →</Link></p></div>
              : hireReqs.map(hr => (
                <div key={hr.id} className="card" style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{hr.seek_ad_title ?? hr.seekAdTitle}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text3)', marginTop: '2px' }}>To: {hr.seeker_name ?? hr.seekerName}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text3)' }}>{new Date(hr.created_at).toLocaleDateString()}</div>
                  </div>
                  <span className={`chip ${hr.status === 'PENDING' ? 'chip-amber' : hr.status === 'ACCEPTED' ? 'chip-green' : 'chip-red'}`}>{hr.status}</span>
                </div>
              ))
          )}
        </div>
      )}

      {/* Applicants Modal */}
      <Modal open={!!applicantJob} onClose={() => setApplicantJob(null)} title={`Applicants — ${applicantJob?.title}`}>
        {applicants.length === 0
          ? <div className="empty-state" style={{ padding: '20px' }}>No applicants yet</div>
          : applicants.map(a => (
            <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--brand-light)', color: 'var(--brand-dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0 }}>
                {a.full_name?.[0]?.toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: '15px' }}>{a.full_name}</div>
                <div style={{ fontSize: '13px', color: 'var(--text2)', whiteSpace: 'pre-wrap', marginBottom: '6px', lineHeight: '1.4' }}>{a.message || 'No message'}</div>
                {a.contact_info && (
                  <div style={{ fontSize: '12px', color: 'var(--brand)', marginBottom: '4px', fontWeight: 600 }}>
                    📞 {a.contact_info}
                  </div>
                )}
                {a.experience_proof && (
                  <div style={{ fontSize: '12px', color: 'var(--text2)', background: 'var(--bg-light)', padding: '6px 10px', borderRadius: '6px', border: '1px dashed var(--border)', marginTop: '6px' }}>
                    💼 <span style={{ fontWeight: 600 }}>Experience/Proof:</span> {a.experience_proof}
                  </div>
                )}
              </div>

              {applicantJob?.status === 'OPEN' && (
                <button className="btn btn-success btn-sm" onClick={() => handleSelect(applicantJob.id, a.user_id)}>
                  {t('select_candidate')}
                </button>
              )}
            </div>
          ))
        }
      </Modal>
      </div>
    </div>
  );
}
